import React, { useState, useEffect } from 'react';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { generateTypeCastSql } from '../utils/sqlGenerator';

interface TypeCastingBuilderProps {
  columnName: string;
  onSqlGenerate: (sql: string) => void;
}

const DATA_TYPES = [
  { value: 'VARCHAR', label: 'VARCHAR (Text)' },
  { value: 'INTEGER', label: 'INTEGER' },
  { value: 'BIGINT', label: 'BIGINT (Large Integer)' },
  { value: 'DOUBLE', label: 'DOUBLE (Decimal)' },
  { value: 'DECIMAL(10,2)', label: 'DECIMAL (Currency)' },
  { value: 'DATE', label: 'DATE' },
  { value: 'TIMESTAMP', label: 'TIMESTAMP' },
  { value: 'TIME', label: 'TIME' },
  { value: 'BOOLEAN', label: 'BOOLEAN' },
];

export const TypeCastingBuilder: React.FC<TypeCastingBuilderProps> = ({
  columnName,
  onSqlGenerate,
}) => {
  const [targetType, setTargetType] = useState<string>('VARCHAR');
  const [safeCast, setSafeCast] = useState<boolean>(false);

  useEffect(() => {
    const sql = generateTypeCastSql(columnName, targetType, safeCast);
    onSqlGenerate(sql);
  }, [targetType, safeCast, columnName, onSqlGenerate]);

  return (
    <div className="space-y-4">
      <div>
        <Label>Target Data Type</Label>
        <Select value={targetType} onValueChange={setTargetType}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {DATA_TYPES.map((type) => (
              <SelectItem key={type.value} value={type.value}>
                {type.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex items-center gap-3">
        <Switch
          id="safe-cast"
          checked={safeCast}
          onCheckedChange={setSafeCast}
        />
        <Label htmlFor="safe-cast" className="cursor-pointer">
          Safe Cast (TRY_CAST - returns NULL on error)
        </Label>
      </div>

      <div className="text-sm text-muted-foreground">
        <strong>Examples:</strong>
        <ul className="list-disc list-inside mt-1 space-y-1">
          <li>VARCHAR to INTEGER: &apos;123&apos; becomes 123</li>
          <li>VARCHAR to DATE: &apos;2024-01-15&apos; becomes DATE &apos;2024-01-15&apos;</li>
          <li>Safe cast: Invalid values become NULL instead of errors</li>
        </ul>
      </div>
    </div>
  );
};
